import { useState, useEffect } from 'react';
import Loader from "../Shared/Loader/Loader";
import APIConstant from "../../utils/pathConstants";
import api from "../../utils/apiClient";
import CommonConfig from '../../utils/constant';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";
import { useCatchResponse } from '../../utils/useCatchResponse';
import { apiBase } from '../../utils/config';

const ExamReport = () => {
    const { catchResponse } = useCatchResponse();
    const loginData = CommonConfig.loginData();
    const [classList, setClassList] = useState([]);
    const [termList, setTermList] = useState([]);
    const [examList, setExamList] = useState([]);
    const [selectedClass, setSelectedClass] = useState(null);
    const [selectedTerm, setSelectedTerm] = useState(null);
    const [selectedExam, setSelectedExam] = useState(null);
    const [subjects, setSubjects] = useState([]);
    const [reportRows, setReportRows] = useState([]);
    const [summary, setSummary] = useState(null);
    const [subjectStats, setSubjectStats] = useState([]);
    const [selectedYear, setSelectedYear] = useState('');
    const [rows, setRows] = useState(10);
    const [first, setFirst] = useState(0);

    useEffect(() => { init(); }, []);

    const scopeId = () => CommonConfig.isOrgOwner(loginData?.UserRole)
        ? loginData?._id : (loginData?.CreatedBy || loginData?._id);

    const init = async () => {
        const years = await CommonConfig.getFinancialYears(loginData?.CreatedBy || loginData?._id);
        const currentYear = CommonConfig.getCurrentFinancialYear(years);
        const cookieYear = CommonConfig.getCookie('selectedAcademicYear');
        const fy = cookieYear ?? currentYear?.value;
        setSelectedYear(fy);
        await GetClassList(fy);
        await GetTermsList();
    };

    const GetClassList = async (fy) => {
        try {
            Loader.show();
            const res = await api.post(APIConstant.path.getClassList, { UserID: scopeId(), FinancialYearId: fy });
            if (res.success) {
                setClassList((res?.data || []).filter(x => x?.Status === 'Active').map(x => ({
                    value: x?._id,
                    label: `${x?.ClassName}-${x?.SectionFullName}-${x?.MediumName}-${x?.Board}`,
                })));
            }
            Loader.hide();
        } catch (err) { Loader.hide(); catchResponse(err); }
    };

    const GetTermsList = async () => {
        try {
            const res = await api.post(APIConstant.path.getTermList, { UserID: scopeId() });
            if (res.success) setTermList((res?.data || []).map(x => ({ label: x?.TermName, value: x?._id })));
        } catch (err) { catchResponse(err); }
    };

    const GetExamList = async (classId) => {
        try {
            const res = await api.post(APIConstant.path.GetResultTemplateListByClassId, {
                ClassId: classId, FinancialYearId: selectedYear, UserId: scopeId(),
            });
            if (res.success) {
                setExamList((res?.data || []).map(x => ({
                    value: x?._id,
                    label: `${x?.Name}${x?.Marks ? ` (${x.Marks})` : ''}`,
                })));
            } else setExamList([]);
        } catch (err) { setExamList([]); }
    };

    const handleClassChange = (value) => {
        setSelectedClass(value);
        setSelectedExam(null);
        setReportRows([]); setSubjects([]); setSummary(null); setSubjectStats([]);
        if (value) GetExamList(value);
    };

    const buildParams = (isExport) => ({
        ClassId: selectedClass,
        ExamTypeId: selectedExam,
        TermId: selectedTerm,
        FinancialYearId: selectedYear,
        UserID: scopeId(),
        ...(isExport ? { Export: 'excel' } : {}),
    });

    const validate = () => {
        if (!selectedClass) { catchResponse({ message: 'Please select a class' }); return false; }
        if (!selectedExam) { catchResponse({ message: 'Please select an exam' }); return false; }
        if (!selectedTerm) { catchResponse({ message: 'Please select a term' }); return false; }
        return true;
    };

    const applyResult = (res) => {
        setSubjects(res?.data?.subjects || []);
        setReportRows(res?.data?.rows || []);
        setSummary(res?.data?.summary || null);
        setSubjectStats(res?.data?.subjectStats || []);
        setFirst(0);
    };

    const handleView = async () => {
        if (!validate()) return;
        try {
            Loader.show();
            const res = await api.post(APIConstant.path.GetExamReport, buildParams(false));
            if (res.success) applyResult(res);
            Loader.hide();
        } catch (err) { Loader.hide(); catchResponse(err); }
    };

    const handleDownload = async () => {
        if (!validate()) return;
        try {
            Loader.show();
            const res = await api.post(APIConstant.path.GetExamReport, buildParams(true));
            if (res.success) {
                applyResult(res);
                if (res?.fileUrl) {
                    const url = `${apiBase}${res.fileUrl}?token=${CommonConfig.fileToken()}`;
                    const link = document.createElement('a');
                    link.href = url;
                    link.setAttribute('download', res.fileUrl.split('/').pop());
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                }
            }
            Loader.hide();
        } catch (err) { Loader.hide(); catchResponse(err); }
    };

    const srNoTemplate = (data, props) => props.rowIndex + 1;
    const resultTemplate = (row) => (
        <span style={{ fontWeight: 'bold', color: row?.Result === 'Pass' ? 'green' : row?.Result === 'Fail' ? 'red' : '#666' }}>
            {row?.Result}
        </span>
    );

    return (
        <div className='wayment-owner-dashboard et-master-page'>
            <div className="heading-area p-d-flex p-ai-center p-jc-between">
                <div className="p-d-flex p-ai-baseline">
                    <i className="pi pi-chart-line"></i>
                    <h2>Exam Report</h2>
                </div>
            </div>

            <div className="p-grid p-ai-end p-mb-3" style={{ gap: '10px', padding: '0 8px' }}>
                <div>
                    <p className="p-mb-1">Class</p>
                    <Dropdown value={selectedClass} options={classList} onChange={(e) => handleClassChange(e.value)}
                        placeholder="Select Class" filter showClear style={{ minWidth: '220px', height: '36px' }} />
                </div>
                <div>
                    <p className="p-mb-1">Exam</p>
                    <Dropdown value={selectedExam} options={examList} onChange={(e) => setSelectedExam(e.value)}
                        placeholder="Select Exam" filter showClear style={{ minWidth: '200px', height: '36px' }} />
                </div>
                <div>
                    <p className="p-mb-1">Term</p>
                    <Dropdown value={selectedTerm} options={termList} onChange={(e) => setSelectedTerm(e.value)}
                        placeholder="Select Term" filter showClear style={{ minWidth: '160px', height: '36px' }} />
                </div>
                <div className="p-d-flex" style={{ gap: '8px' }}>
                    <Button label="View" icon="pi pi-search" className="btn-text-action-primary" onClick={handleView} />
                    <Button label="Download Excel" icon="pi pi-file-excel" className="btn-text-action-secondary" onClick={handleDownload} />
                </div>
            </div>

            {summary && (
                <div className="p-mb-2" style={{ padding: '0 8px', fontSize: '0.9rem' }}>
                    <strong>{summary?.className}</strong>
                    {`  |  ${summary?.examName}${summary?.termName ? ' / ' + summary.termName : ''}`}
                    {`  |  Appeared: ${summary?.appeared}/${summary?.totalStudents}  |  Pass: ${summary?.passCount}  Fail: ${summary?.failCount}  |  Class Avg: ${summary?.classAverage}%`}
                    {summary?.topper ? `  |  Topper: ${summary.topper.StudentName} (${summary.topper.Obtained}/${summary.topper.MaxTotal})` : ''}
                </div>
            )}

            <DataTable
                value={reportRows}
                paginator rows={rows} first={first}
                onPage={(e) => { setFirst(e.first); setRows(e.rows); }}
                rowsPerPageOptions={[10, 20, 50]}
                paginatorTemplate="RowsPerPageDropdown CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink"
                currentPageReportTemplate="Showing {first}-{last} of {totalRecords} records"
                responsiveLayout="scroll" className='custom-table' scrollHeight='calc(100vh - 300px)' size='small'
                emptyMessage="No data. Select class, exam, term and click View.">
                <Column body={srNoTemplate} header="Sr No." frozen />
                <Column field="RollNo" header="Roll No." />
                <Column field="StudentName" header="Student Name" />
                {subjects.map((s) => (
                    <Column key={s._id} header={`${s.SubjectName}${s.MaxMarks ? ` (${s.MaxMarks})` : ''}`}
                        body={(row) => row?.Subjects?.[s._id]} />
                ))}
                <Column field="Obtained" header="Total" />
                <Column field="MaxTotal" header="Max" />
                <Column header="Percentage" body={(row) => `${row?.Percentage}%`} />
                <Column field="Grade" header="Grade" />
                <Column body={resultTemplate} header="Result" />
                <Column field="Rank" header="Rank" />
            </DataTable>
        </div>
    );
};

export default ExamReport;
